import type { CSSProperties, FC } from 'react';
import type { StudentKPI, StudentProfile } from '../types';
import {
  BookIcon,
  BuildingIcon,
  CalendarIcon,
  ProjectIcon,
  SearchIcon,
  StarIcon,
  ToolIcon,
  UsersIcon,
} from './CourseStudentIcons';

interface CourseStudentCabinetGridProps {
  profile: StudentProfile | null;
  kpi: StudentKPI | null;
  onOpenWorkspace: () => void;
  onOpenLiterature: () => void;
  onOpenConnections: () => void;
  onOpenTools: () => void;
  onOpenMeetings: () => void;
  onOpenScientificProjects: () => void;
  onOpenOpportunities: () => void;
  onOpenOfficialSources: () => void;
  onOpenSuperAI: () => void;
  onOpenCreateRequest?: () => void;
}

export const CourseStudentCabinetGrid: FC<CourseStudentCabinetGridProps> = ({
  profile,
  kpi,
  onOpenWorkspace,
  onOpenLiterature,
  onOpenConnections,
  onOpenTools,
  onOpenMeetings,
  onOpenScientificProjects,
  onOpenOpportunities,
  onOpenOfficialSources,
  onOpenSuperAI,
  onOpenCreateRequest,
}) => {
  if (!profile || !kpi) {
    return (
      <div className="space-y-6 pb-12">
        <p className="text-sm text-slate-400">Загрузка кабинета...</p>
      </div>
    );
  }

  const currentProfile = profile;
  const currentKPI = kpi;

  const initials =
    currentProfile.full_name
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || 'СТ';

  const moduleCards = [
    {
      num: '01',
      title: 'Учебный раздел',
      desc: 'Дисциплины, недели, СРО / СРОП, РК1 / РК2, оценки и посещаемость.',
      icon: BookIcon,
      onClick: onOpenWorkspace,
      accent: '#41d2ff',
    },
    {
      num: '02',
      title: 'Найти литературу',
      desc: 'Поиск академических источников и личная библиотека.',
      icon: SearchIcon,
      onClick: onOpenLiterature,
      accent: '#49ddb7',
    },
    {
      num: '03',
      title: 'Академические связи',
      desc: 'Преподаватель, староста и обучающиеся вашей группы.',
      icon: UsersIcon,
      onClick: onOpenConnections,
      accent: '#e2ad3a',
    },
    {
      num: '04',
      title: 'Программы и инструменты',
      desc: 'Опросы, эксперименты и исследовательские инструменты академии.',
      icon: ToolIcon,
      onClick: onOpenTools,
      accent: '#9e80ff',
    },
    {
      num: '05',
      title: 'Встречи и консультации',
      desc: 'Консультации, СРОП и календарь обучающегося.',
      icon: CalendarIcon,
      onClick: onOpenMeetings,
      accent: '#ff7a64',
    },
    {
      num: '06',
      title: 'Научные проекты',
      desc: 'План, этапы, задачи, материалы и результаты исследований.',
      icon: ProjectIcon,
      onClick: onOpenScientificProjects,
      accent: '#5da8ff',
    },
    {
      num: '07',
      title: 'Возможности и развитие',
      desc: 'Конференции, публикации, мобильность и конкурсы.',
      icon: StarIcon,
      onClick: onOpenOpportunities,
      accent: '#e2ad3a',
    },
    {
      num: '08',
      title: 'Официальные источники ENU',
      desc: 'Подтверждённые ENU-кабинеты, маршруты и процедуры.',
      icon: BuildingIcon,
      onClick: onOpenOfficialSources,
      accent: '#66b7e8',
    },
  ];

  return (
    <div className="space-y-6 pb-12">
      <div className="role-hero" style={{ '--role-accent': '#49ddb7' } as CSSProperties}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-400 flex items-center justify-center text-slate-950 font-black text-lg shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <small>
              {currentProfile.academic_id} · {currentProfile.group_code} · Обучающийся
            </small>
            <h2 className="truncate">{currentProfile.full_name}</h2>
            <p>
              {currentProfile.program_name} · {currentProfile.course_year} курс ·{' '}
              {currentProfile.status}
            </p>
          </div>
        </div>
        <div className="role-hero-actions">
          <button type="button" className="role-btn" onClick={onOpenSuperAI}>
            Super AI
          </button>
          {onOpenCreateRequest ? (
            <button type="button" className="role-btn primary" onClick={onOpenCreateRequest}>
              + Создать заявку
            </button>
          ) : null}
        </div>
      </div>

      <div className="role-statgrid">
        <button type="button" className="role-stat" onClick={onOpenWorkspace}>
          <strong>{currentKPI.disciplines}</strong>
          <span>Дисциплины</span>
        </button>
        <button type="button" className="role-stat" onClick={onOpenWorkspace}>
          <strong>{currentKPI.assignments}</strong>
          <span>Задания</span>
        </button>
        <button type="button" className="role-stat" onClick={onOpenWorkspace}>
          <strong>{currentKPI.progress}%</strong>
          <span>Прогресс</span>
        </button>
        <button type="button" className="role-stat" onClick={onOpenWorkspace}>
          <strong>{currentKPI.deadlines}</strong>
          <span>Дедлайны</span>
        </button>
      </div>

      <div className="owner-hub-top-actions">
        <button type="button" className="owner-hub-ai-action" onClick={onOpenSuperAI}>
          <span className="spark" aria-hidden="true">
            ✦
          </span>
          <span>
            <strong>Super AI</strong>
            <small>Помощник по функциям «Обучающийся» и разрешённым данным</small>
          </span>
          <b>Открыть →</b>
        </button>
        {onOpenCreateRequest ? (
          <button type="button" className="owner-hub-create" onClick={onOpenCreateRequest}>
            + Создать заявку
          </button>
        ) : null}
      </div>

      <div>
        <div className="mb-4">
          <h3 className="text-base font-bold text-slate-200">Разделы кабинета</h3>
          <p className="text-xs text-slate-400 mt-0.5">8 рабочих контуров обучающегося</p>
        </div>

        <div className="owner-hub-grid">
          {moduleCards.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.num}
                type="button"
                onClick={card.onClick}
                className="owner-hub-card"
                style={{ '--action': card.accent } as CSSProperties}
              >
                <span className="owner-hub-index">{card.num}</span>
                <span className="owner-hub-icon" aria-hidden="true">
                  <Icon className="w-5 h-5" />
                </span>
                <span className="owner-hub-copy">
                  <strong>{card.title}</strong>
                  <span>{card.desc}</span>
                </span>
                <span className="owner-hub-arrow" aria-hidden="true">
                  ›
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
